// Types
export interface CategoryRating {
  text: string;
  value: number;
}

export interface Report {
  name: string;
  categoryRatings: Record<string, CategoryRating>;
  criticalReason: string;
  totalValue: number;
}

// Categories
export const categories = [
  'Conversion Optimization',
  'Trust Building',
  'Value Perception',
  'Decision Support',
  'User Experience',
  'Customer Understanding',
  'Competitive Positioning',
  'Channel Integration',
  'Acquisition',
  'Retention',
  'Messaging & Communication',
  'Personalization'
];

// Reports data
export const reports: Report[] = [
  {
    name: 'Conversion Funnel & Abandonment Analysis',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓✓', value: 3 },
      'Trust Building': { text: '✓', value: 1 },
      'Value Perception': { text: '✓', value: 1 },
      'Decision Support': { text: '✓✓', value: 2 },
      'User Experience': { text: '✓', value: 1 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Directly targets the universal pain point of abandonment that affects all CX professionals. Reveals the psychological reasons behind customer hesitation, enabling targeted interventions that address root causes rather than symptoms.',
    totalValue: 1.6
  },
  {
    name: 'First Impression Psychology Assessment',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓✓', value: 3 },
      'Trust Building': { text: '✓✓', value: 2 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '✓✓✓', value: 3 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '✓✓', value: 2 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Addresses the crucial 3-8 second window that determines all subsequent engagement. A poor first impression creates abandonment that no downstream optimization can fix. Focuses on an area most CX professionals overlook while optimizing full journeys.',
    totalValue: 2.85
  },
  // Add all other reports here following the same pattern...
  {
    name: 'Value & Price Psychology Framework',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓✓', value: 3 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '✓✓✓', value: 3 },
      'Decision Support': { text: '✓✓', value: 2 },
      'Competitive Positioning': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'User Experience': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Solves the universal challenge of price objections without resorting to margin-damaging discounts. Transforms pricing from an economic discussion to a psychological one, enabling premium positioning and higher conversion without sacrificing margins.',
    totalValue: 2.4
  },
  {
    name: 'Trust Ecosystem Architecture',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓', value: 2 },
      'Trust Building': { text: '✓✓✓', value: 3 },
      'Decision Support': { text: '✓', value: 1 },
      'User Experience': { text: '✓', value: 1 },
      'Acquisition': { text: '✓', value: 1 },
      'Value Perception': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Creates a unified framework connecting trust dimensions with appropriate social validation mechanisms. Standard trust badges and elements often fail to address the specific psychological trust dimensions that truly matter at each journey stage.',
    totalValue: 1.55
  },
  {
    name: 'Decision Psychology Optimization',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓✓', value: 3 },
      'Decision Support': { text: '✓✓✓', value: 3 },
      'User Experience': { text: '✓✓', value: 2 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Addresses the complex psychological factors (cognitive biases, competing tensions, objection patterns) that determine choices regardless of logical benefits, resolving the baffling decision behavior that frustrates CX professionals.',
    totalValue: 2.35
  },
  {
    name: 'Psychological Safety in Customer Experience',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓', value: 2 },
      'Trust Building': { text: '✓✓✓', value: 3 },
      'Decision Support': { text: '✓', value: 1 },
      'User Experience': { text: '✓✓', value: 2 },
      'Value Perception': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Addresses invisible vulnerability causing abandonment at points requiring personal information or commitment. Standard usability and security measures miss the deeper psychological comfort needs that determine whether customers will share information.',
    totalValue: 1.95
  },
  {
    name: 'Mobile Experience Psychology Profile',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓', value: 2 },
      'User Experience': { text: '✓✓✓', value: 3 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Channel Integration': { text: '✓✓', value: 2 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Targets the persistent mobile conversion gap with mobile-specific psychological optimizations. Mobile usage occurs in fundamentally different psychological contexts than desktop, requiring unique approaches beyond responsive design.',
    totalValue: 1.95
  },
  {
    name: 'Customer Effort Score Psychology',
    categoryRatings: {
      'Conversion Optimization': { text: '✓', value: 1 },
      'User Experience': { text: '✓✓✓', value: 3 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Retention': { text: '✓✓', value: 2 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Resolves the disconnect between process optimization and customer perception of effort. Perceived effort is primarily psychological rather than technical, explaining why process simplifications often fail to improve effort scores despite reducing actual steps.',
    totalValue: 1.5
  },
  {
    name: 'Customer Tension Framework',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓', value: 2 },
      'Value Perception': { text: '✓', value: 1 },
      'Decision Support': { text: '✓✓✓', value: 3 },
      'Customer Understanding': { text: '✓✓', value: 2 },
      'Trust Building': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Explains contradictory customer behavior by revealing competing desires creating purchase hesitation. Addresses the baffling situation where customers engage deeply but mysteriously abandon, providing psychological resolution strategies.',
    totalValue: 1.95
  },
  {
    name: 'Brand Perception Reality Check',
    categoryRatings: {
      'Conversion Optimization': { text: '✓', value: 1 },
      'Trust Building': { text: '✓', value: 1 },
      'Value Perception': { text: '✓', value: 1 },
      'User Experience': { text: '✓', value: 1 },
      'Customer Understanding': { text: '✓✓✓', value: 3 },
      'Competitive Positioning': { text: '✓✓', value: 2 },
      'Acquisition': { text: '✓', value: 1 },
      'Retention': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓✓', value: 2 },
      'Decision Support': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Reveals disconnects between assumed and actual brand perception. Internal brand assumptions often significantly diverge from customer reality, creating fundamental misalignments that technical optimization alone can\'t solve.',
    totalValue: 2.2
  },
  {
    name: 'Competitive Advantage & Vulnerability Analysis',
    categoryRatings: {
      'Value Perception': { text: '✓', value: 1 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '✓✓✓', value: 3 },
      'Acquisition': { text: '✓✓', value: 2 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Transforms vague competitive anxiety into strategic clarity with specific opportunities to create meaningful differentiation. Provides both defensive positioning to leverage your psychological advantages and offensive strategy to exploit competitor vulnerabilities.',
    totalValue: 1.5
  },
  {
    name: 'Message Gap Analysis',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓', value: 2 },
      'Value Perception': { text: '✓', value: 1 },
      'User Experience': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '✓✓', value: 2 },
      'Acquisition': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓✓✓', value: 3 },
      'Trust Building': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Identifies psychological communication territories competitors have abandoned or overlooked. Transforms messaging from incremental A/B testing to strategically addressing psychological gaps, creating breakthrough conversion where minor variations have failed.',
    totalValue: 2.05
  },
  {
    name: 'Churn Risk & Retention Analysis',
    categoryRatings: {
      'Customer Understanding': { text: '✓✓', value: 2 },
      'Retention': { text: '✓✓✓', value: 3 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Transforms approach from reactive (responding to cancellations) to proactive (addressing psychological factors before they lead to churn). Identifies the emotional triggers and unmet needs that lead to disengagement long before cancellation.',
    totalValue: 1.45
  },
  {
    name: 'B2B Decision Psychology Mapping',
    categoryRatings: {
      'Conversion Optimization': { text: '✓✓', value: 2 },
      'Trust Building': { text: '✓', value: 1 },
      'Decision Support': { text: '✓✓✓', value: 3 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Acquisition': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Value Perception': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Addresses the complex multi-stakeholder decision processes in B2B contexts. Reveals the hidden psychological dynamics of organizational decision-making that determine outcomes regardless of individual engagement.',
    totalValue: 1.6
  },
  {
    name: 'Voice-of-Customer Emotional Analytics',
    categoryRatings: {
      'User Experience': { text: '✓', value: 1 },
      'Customer Understanding': { text: '✓✓✓', value: 3 },
      'Retention': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓✓', value: 2 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Transforms overwhelming, unstructured feedback into clear psychological insights for action. Reveals the underlying emotional currents driving satisfaction and dissatisfaction, enabling experience design that addresses root psychological needs.',
    totalValue: 1.5
  },
  {
    name: 'Integrated Journey Experience',
    categoryRatings: {
      'Conversion Optimization': { text: '✓', value: 1 },
      'Decision Support': { text: '✓', value: 1 },
      'User Experience': { text: '✓✓', value: 2 },
      'Channel Integration': { text: '✓✓✓', value: 3 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Personalization': { text: '✓', value: 1 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 }
    },
    criticalReason: 'Creates unified psychological journey design across touchpoints and channels. Addresses both the psychological factors influencing decisions between touchpoints and the fundamental shifts in customer psychology when moving between channels.',
    totalValue: 1.6
  },
  {
    name: 'Persona & Psychographic Profile Report',
    categoryRatings: {
      'User Experience': { text: '✓', value: 1 },
      'Customer Understanding': { text: '✓✓✓', value: 3 },
      'Acquisition': { text: '✓', value: 1 },
      'Retention': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Personalization': { text: '✓✓', value: 2 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 }
    },
    criticalReason: 'Transforms flat behavioral segments into multi-dimensional psychological profiles. Provides the deeper customer understanding that enables truly resonant experiences for different customer types based on motivations, values, and decision styles.',
    totalValue: 1.6
  },
  {
    name: 'Loyalty Psychology & Driver Hierarchy',
    categoryRatings: {
      'Value Perception': { text: '✓', value: 1 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Retention': { text: '✓✓✓', value: 3 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Personalization': { text: '✓', value: 1 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 }
    },
    criticalReason: 'Maps and prioritizes the specific psychological factors driving long-term engagement and advocacy. Expands capability from conversion optimization to comprehensive customer lifecycle management.',
    totalValue: 1.1
  },
  {
    name: 'Category Expectation Benchmarking',
    categoryRatings: {
      'Trust Building': { text: '✓', value: 1 },
      'Value Perception': { text: '✓', value: 1 },
      'User Experience': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '✓✓✓', value: 3 },
      'Conversion Optimization': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Acquisition': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Reveals the standards customers unconsciously apply based on category experience. Addresses fear of competitive disadvantage by providing clear category benchmarks and identifying where experiences fall short of expectations.',
    totalValue: 1.05
  },
  {
    name: 'Strategic Market Intelligence',
    categoryRatings: {
      'Customer Understanding': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '✓✓✓', value: 3 },
      'Acquisition': { text: '✓', value: 1 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Messaging & Communication': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Provides forward-looking competitive intelligence from a psychological perspective. Identifies industry-wide blind spots, anticipates competitor responses to initiatives, and forecasts emerging psychological trends before they become obvious.',
    totalValue: 1.0
  },
  {
    name: 'Psychological Differentiation Strategy',
    categoryRatings: {
      'Value Perception': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '✓✓✓', value: 3 },
      'Acquisition': { text: '✓', value: 1 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Customer Understanding': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 },
      'Personalization': { text: '', value: 0 }
    },
    criticalReason: 'Addresses long-term differentiation in increasingly commoditized markets. Identifies specific psychological territories competitors have overlooked that align with unique organizational capabilities, creating sustainable competitive advantage.',
    totalValue: 1.05
  },
  {
    name: 'Personalization & Messaging Insights',
    categoryRatings: {
      'Conversion Optimization': { text: '✓', value: 1 },
      'Decision Support': { text: '✓', value: 1 },
      'User Experience': { text: '✓✓', value: 2 },
      'Customer Understanding': { text: '✓', value: 1 },
      'Channel Integration': { text: '✓', value: 1 },
      'Acquisition': { text: '✓✓', value: 2 },
      'Messaging & Communication': { text: '✓✓', value: 2 },
      'Personalization': { text: '✓✓✓', value: 3 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Competitive Positioning': { text: '', value: 0 },
      'Retention': { text: '', value: 0 }
    },
    criticalReason: 'Transforms personalization from mechanical rule application to psychological insight. Enables personalization that addresses specific emotional states, decision styles, and information needs, creating experiences that feel intuitively right to each customer.',
    totalValue: 2.6
  },
  {
    name: 'Psychological Acquisition Strategy',
    categoryRatings: {
      'Customer Understanding': { text: '✓', value: 1 },
      'Competitive Positioning': { text: '✓', value: 1 },
      'Acquisition': { text: '✓✓✓', value: 3 },
      'Messaging & Communication': { text: '✓', value: 1 },
      'Personalization': { text: '✓✓', value: 2 },
      'Conversion Optimization': { text: '', value: 0 },
      'Trust Building': { text: '', value: 0 },
      'Value Perception': { text: '', value: 0 },
      'Decision Support': { text: '', value: 0 },
      'User Experience': { text: '', value: 0 },
      'Channel Integration': { text: '', value: 0 },
      'Retention': { text: '', value: 0 }
    },
    criticalReason: 'Addresses rising acquisition costs with targeted psychological tactics. Identifies specific customer segments most receptive to your offering based on psychological dissatisfaction with current providers.',
    totalValue: 1.55
  }
];